/**
 * bridgeRelationships - Constants and classifiers for the Teamwork Graph
 * "bridge" relationships that the graph explorer hides as an internal
 * implementation detail.
 *
 * A bridge relationship connects an Atlassian-side entity to a third-party /
 * external counterpart so that the graph can run permission checks against
 * the Atlassian identity / entity. From the user's perspective, however,
 * the bridge node and the bridge edge should never appear — the user just
 * sees a "real" connection between the two real entities.
 *
 * Two bridges are currently modelled:
 *
 *   1. AtlassianUser ←→ ThirdPartyUser
 *      via `atlassian_user_links_external_user`
 *      All ThirdPartyUser nodes are projected back to their AtlassianUser
 *      peer when surfaced, and ThirdPartyUser-rooted relationships are
 *      surfaced from the AtlassianUser side via a multi-hop query.
 *
 *   2. ThirdParty (`ati:third-party:*`) ←→ External graph entity
 *      via `external_entity_is_third_party_entity`
 *      All `ati:third-party:*` nodes are projected back to their
 *      `ari:cloud:graph::*` (External*) peer when surfaced.
 *
 * Both bridges are referenced by name throughout the discovery & display
 * pipeline and live here so that adding/changing one is a single edit.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * AtlassianUser ↔ ThirdPartyUser bridge.
 *
 *   AtlassianUser  -[atlassian_user_links_external_user]->  ThirdPartyUser
 *
 * `bridgeTypename` is the canonical TWG node name we want to hide; the
 * surface is the `realTypename` peer.
 */
export const ATLASSIAN_USER_BRIDGE = {
	relName: 'atlassian_user_links_external_user',
	bridgeTypename: 'ThirdPartyUser',
	realTypename: 'AtlassianUser',
} as const;

/**
 * ThirdParty ↔ External graph entity bridge.
 *
 *   <real ati:cloud:graph node>  -[external_entity_is_third_party_entity]->  <ati:third-party node>
 *
 * Any node whose ATI begins with `ati:third-party` is considered a bridge
 * node for the purposes of this relationship. The surface is the
 * `ati:cloud:graph` peer (which is what the user already sees in the graph
 * as `External*` typenames).
 */
export const EXTERNAL_ENTITY_BRIDGE = {
	relName: 'external_entity_is_third_party_entity',
	bridgePrefix: 'ati:third-party',
	realPrefix: 'ari:cloud:graph',
	realAtiPrefix: 'ati:cloud:graph',
} as const;

/**
 * All bridge relationship names. Useful for display-layer filtering when we
 * want to suppress every bridge rel without listing them individually.
 */
export const BRIDGE_RELATIONSHIP_NAMES: readonly string[] = [
	ATLASSIAN_USER_BRIDGE.relName,
	EXTERNAL_ENTITY_BRIDGE.relName,
];

// ---------------------------------------------------------------------------
// Predicates
// ---------------------------------------------------------------------------

/**
 * Returns true when the supplied ARI or ATI string identifies a node sitting
 * on the third-party side of the External bridge.
 *
 * Both schemes must be checked:
 *   - ATI form: `ati:third-party:github.github:repository`  (type identifier)
 *   - ARI form: `ari:third-party:google.google-drive::document/...`  (node ARI)
 *
 * The ARI form arises when the graph store returns the raw third-party node
 * ARI directly from a Cypher query (e.g. from the direct arm of the
 * content-entity-links probe before the bridge hop has resolved it to the
 * External peer).
 */
export function isAtiThirdParty(ati: string | null | undefined): boolean {
	if (!ati) return false;
	// ATI form: ati:third-party:...
	if (ati.startsWith(`${EXTERNAL_ENTITY_BRIDGE.bridgePrefix}:`)) return true;
	// ARI form: ari:third-party:...
	if (ati.startsWith('ari:third-party:')) return true;
	return false;
}

/**
 * Returns true when the supplied ATI / typename refers to a `ThirdPartyUser`
 * node (the AtlassianUser bridge target).
 */
export function isThirdPartyUserType(typeOrAti: string | null | undefined): boolean {
	if (!typeOrAti) return false;
	if (typeOrAti === ATLASSIAN_USER_BRIDGE.bridgeTypename) return true;
	// ATI form for the bridge typename — defensive.
	return typeOrAti.endsWith(':third-party-user') || typeOrAti.endsWith(':thirdpartyuser');
}

// ---------------------------------------------------------------------------
// Target-type classification
// ---------------------------------------------------------------------------

export type BridgeShape =
	/** Every supplied target is a third-party (multi-hop required for all). */
	| 'all-third-party'
	/** Every supplied target is a normal cloud type (no bridge handling needed). */
	| 'all-cloud'
	/** Mix of third-party + cloud targets (per-target discriminated MATCH). */
	| 'mixed'
	/** No usable targets (e.g. wildcard `*` only). */
	| 'none';

/**
 * Classifies a relationship's target/from type list to decide which Cypher
 * topology the discovery builder should emit for it.
 *
 * Wildcard (`*`) entries are ignored — they cannot drive a discriminated
 * MATCH.
 */
export function classifyTargetTypes(targets: readonly string[] | undefined): BridgeShape {
	const usable = (targets ?? []).filter((t) => !!t && t !== '*');
	if (usable.length === 0) return 'none';

	let hasThirdParty = false;
	let hasCloud = false;
	for (const ati of usable) {
		if (isAtiThirdParty(ati)) {
			hasThirdParty = true;
		} else {
			hasCloud = true;
		}
		if (hasThirdParty && hasCloud) return 'mixed';
	}

	if (hasThirdParty) return 'all-third-party';
	return 'all-cloud';
}

/**
 * Splits a target/from type list into its third-party and cloud halves.
 * The returned arrays preserve the original order and exclude wildcards.
 */
export function partitionTargetTypes(targets: readonly string[] | undefined): {
	thirdPartyAtis: string[];
	cloudAtis: string[];
} {
	const thirdPartyAtis: string[] = [];
	const cloudAtis: string[] = [];
	for (const ati of targets ?? []) {
		if (!ati || ati === '*') continue;
		if (isAtiThirdParty(ati)) {
			thirdPartyAtis.push(ati);
		} else {
			cloudAtis.push(ati);
		}
	}
	return { thirdPartyAtis, cloudAtis };
}
